import React, { useState, useEffect, useRef } from 'react'
import { 
  useStripe, 
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement
} from '@stripe/react-stripe-js'
import { ajaxPost } from '../../ajax'
import USD from '../usd'

export default function Form(props) {
  const { currency, amount, appFee, token, location, storeOnly, storeOption, game, player,
    serverError, onClose, onPayment, onCardsUpdate } = props
  const stripe = useStripe()
  const elements = useElements()
  const [filled, setFilled] = useState({})
  const [complete, setComplete] = useState({})
  const [focused, setFocused] = useState('')
  const [error, setError] = useState(null)
  const [processing, setProcessing] = useState(false)
  const [brand, setBrand] = useState('unknown')
  const currencyName = currency == 'usd' ? '' : ` (${currency.toUpperCase()})`
  const formCompleted = complete.card && complete.date && complete.cvc
  const submitText = storeOnly ? <span>Store Card</span> : <span>{`Pay${currencyName} `}<USD val={amount} /></span>
  const options = {
    style: {
      base: {
        fontWeight: 'lighter',
        fontSize: '16px',
      },
    },
  }
  const timer = useRef()

  useEffect(() => {
    onError(serverError)
  }, [serverError])

  const handleSubmit = () => {
    startProcessing()

    if (!stripe || !elements) return

    storeOnly ? storePayment() : processPayment()
  }

  const onSubmit = () => {
    clearTimeout(timer.current)
    timer.current = setTimeout(() => handleSubmit(), 200)
  }

  const processPayment = async () => {
    const intent = await createIntent()
    if (intent.error) {
      onError(intent.error)
      return
    }

    if (intent.secret == 'pi_test_valid') {
      return onPayment({type: 'card_number', token: intent.secret})
    }

    const result = await stripe.confirmCardPayment(intent.secret, {
      payment_method: { card: elements.getElement(CardNumberElement) }
    })

    if (result.error) {
      onError(result.error.message)
    } else {
      if (result.paymentIntent.status === 'requires_capture') {
        onPayment({type: 'card_number', token: result.paymentIntent.id})
      }
    }
  }

  const createIntent = async () => {
    const params = { 
      currency: currency,
      amount: amount.toFixed(2),
      app_fee: appFee.toFixed(2),
      location_id: location
    }
    if (token) params.token = token
    const path = token ? '/components/payment/create_intent' : '/admins/payment/create_intent'
    const response = await ajaxPost(path, params)
    const json = await response.json()
    return json.intent
  }

  const onStorePayment = (event) => {
    event.preventDefault()
    startProcessing()
    storePayment()
  }

  const storePayment = async () => {
    const result = await stripe.createPaymentMethod({
      type: 'card',
      card: elements.getElement(CardNumberElement)
    })

    if (result.error)
      onError(result.error.message)
    else {
      const token = result.paymentMethod.id
      storeOnly && onPayment({type: 'card_number', token: token})
      storeOption && storeCard(token)
    }
  }

  const storeCard = async (token) => {
    const data = { reservation_id: game, player_id: player, token: token }
    const response = await ajaxPost('/admins/payment/store_card', data)
    const json = await response.json()
    json.error ? onError(json.error) : updateCards(json.cards)
  }

  const updateCards = (cards) => {
    onClose()
    onCardsUpdate(cards)
  }

  const startProcessing = () => {
    setProcessing(true)
    setError(null)
  }

  const onError = (error) => {
    setError(error)
    setProcessing(false)
  }

  const onElementChange = (event, name) => {
    if (name == 'card') setBrand(event.brand)
    setFilled({...filled, [name]: !event.empty})
    setComplete({...complete, [name]: event.complete})
  }

  return (
    <form className='stripe-form' onSubmit={e => {e.preventDefault(); onSubmit()}}>
      { processing && <div className='animated-spinner'></div>}
      { error && <label className='stripe-error'>{error}</label> }
      <div className='stripe-form-card-number-img'>
        <label className={'payment-element-label' + (focused == 'card' ? ' active' : '')} htmlFor='cardnumber'>
          <span className={'payment-element-name' + (filled.card ? ' active' : '')}>Card Number</span>
          <CardNumberElement
            options={{...options, placeholder: 'Card Number'}}
            onChange={(event) => onElementChange(event, 'card')}
            onFocus={() => setFocused('card')}
            onBlur={() => setFocused('')}
          />
        </label>
        <div className='payment-element-img'>
          <div className={'brand-img' + ` ${brand}-img`}></div>
        </div>
      </div>
      <div className='stripe-form-expiration-cvc'>
        <label className={'payment-element-label' + (focused == 'date' ? ' active' : '')} htmlFor='exp-date'>
          <span className={'payment-element-name' + (filled.date ? ' active' : '')}>MM / YY</span>
          <CardExpiryElement
            options={{...options, placeholder: 'Expiration Date'}}
            onChange={(event) => onElementChange(event, 'date')}
            onFocus={() => setFocused('date')}
            onBlur={() => setFocused('')}
          />
        </label>
        <label className={'payment-element-label' + (focused == 'cvc' ? ' active' : '')} htmlFor='cvc'>
          <span className={'payment-element-name' + (filled.cvc ? ' active' : '')}>CVV (3 digits)</span>
          <CardCvcElement
            options={{...options, placeholder: 'CVV'}}
            onChange={(event) => onElementChange(event, 'cvc')}
            onFocus={() => setFocused('cvc')}
            onBlur={() => setFocused('')}
          />
        </label>
      </div>
      <button className='btn btn-success pull-left' type='submit' disabled={!stripe || !formCompleted || processing}>
        {submitText}
      </button>
      { storeOption &&
        <button 
          className='btn btn-info pull-left'
          disabled={!stripe || !formCompleted || processing}
          onClick={onStorePayment}
        >
          Store Card
        </button>
      }
      <button className='btn btn-info pull-right' onClick={(e) => { e.preventDefault(); onClose() }}>
        Cancel
      </button>
    </form>
  )
}
